import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const ACTION_COLORS = {
  login_success:  { bg: '#1a3a2a', color: '#4ade80' },
  login_failure:  { bg: '#2a1f1f', color: '#f87171' },
  logout:         { bg: '#1e2a3a', color: '#60a5fa' },
  user_created:   { bg: '#2d1f3d', color: '#c084fc' },
  bot_created:    { bg: '#2d2a1a', color: '#fbbf24' },
  bot_updated:    { bg: '#2d2a1a', color: '#fbbf24' },
  bot_deleted:    { bg: '#2a1f1f', color: '#f87171' },
};

const ACTIONS = ['', 'login_success', 'login_failure', 'logout', 'user_created', 'bot_created', 'bot_updated', 'bot_deleted'];

function fmt(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'medium' });
}

export default function AuditLogs() {
  const [logs, setLogs]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [action, setAction]   = useState('');
  const [page, setPage]       = useState(1);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (action) params.set('action', action);
      const { data } = await api.get(`/api/audit-logs?${params}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch { setLogs([]); }
    finally { setLoading(false); }
  }, [action, page]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.ceil(total / limit);

  return (
    <div style={{ padding: 24, background: '#0f172a', minHeight: '100vh', color: '#f1f5f9' }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Audit Logs</h1>
          <p style={{ fontSize: 13, color: '#64748b' }}>{total} events • auto-expire after 90 days</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select
            value={action}
            onChange={e => { setAction(e.target.value); setPage(1); }}
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}
          >
            {ACTIONS.map(a => <option key={a} value={a}>{a || 'All actions'}</option>)}
          </select>
          <button onClick={load} style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#1e293b', borderRadius: 12, border: '1px solid #334155', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #334155' }}>
              {['Timestamp', 'Action', 'User', 'IP Address', 'Resource', 'Details'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading…</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No logs found.</td></tr>
            ) : logs.map(log => {
              const ac = ACTION_COLORS[log.action] || { bg: '#1e293b', color: '#94a3b8' };
              return (
                <tr key={log._id} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '12px 16px', color: '#94a3b8', whiteSpace: 'nowrap', fontSize: 12 }}>{fmt(log.createdAt)}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: ac.bg, color: ac.color, padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                      {log.action}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', color: '#cbd5e1', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.email || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#94a3b8', fontFamily: 'monospace', fontSize: 12 }}>
                    {log.ip || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 12 }}>
                    {log.resource ? `${log.resource}${log.resourceId ? ` #${log.resourceId.slice(-6)}` : ''}` : '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.meta ? JSON.stringify(log.meta) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '6px 14px', background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer' }}>← Prev</button>
          <span style={{ padding: '6px 14px', color: '#64748b', fontSize: 13 }}>Page {page} of {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
            style={{ padding: '6px 14px', background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer' }}>Next →</button>
        </div>
      )}
    </div>
  );
}
